import { Avatar, Badge, Box, Chip, List, ListItemAvatar, ListItemButton, ListItemText, Stack, Typography } from "@mui/material"
import ForumIcon from "@mui/icons-material/ForumOutlined"
import TopicIcon from "@mui/icons-material/LabelOutlined"
import GroupIcon from "@mui/icons-material/GroupsOutlined"
import SupportIcon from "@mui/icons-material/SupportAgentOutlined"

import EmptyState from "../../components/EmptyState"
import { LoadingRows } from "../../components/LoadingState"
import { avatarColor, initialsOf, relativeTime } from "../../utils/format"

const KIND_ICON = { topic: TopicIcon, group: GroupIcon, support: SupportIcon }

/**
 * A room's display title.
 *
 * A DM has none stored — the title is the other person, resolved server-side per
 * viewer. Storing it would go stale the moment somebody is renamed, and would be
 * wrong for one of the two participants from the start.
 */
function titleFor(room) {
  if (room.kind === "dm") return room.peer?.name || room.peer?.email || "Direct message"
  return room.topic || room.title || "Conversation"
}

/**
 * What an empty inbox says under a filter.
 *
 * An inbox with nothing in it and an inbox you have filtered down to nothing are
 * different facts, and the second one is far more common: read everything, click
 * Unread, and the app used to offer "Start a direct message" — an action whose
 * result would not match the filter you are standing in, so the list would still
 * be empty afterwards. The way out of a filtered-empty list is to leave the
 * filter.
 */
const FILTERED_EMPTY = {
  unread: "Everything here has been read.",
  topic: "No topic threads yet.",
  dm: "No direct messages yet.",
  support: "No support threads yet.",
  clients: "No conversations with a client yet.",
  internal: "No conversations inside the back office yet.",
}

export default function ChatRoomList({ rooms, loading, activeId, filter = "all", onClearFilter, onSelect, onCreate }) {
  if (loading) {
    return (
      <Box sx={{ p: 1.5 }}>
        <LoadingRows rows={6} height={58} />
      </Box>
    )
  }

  if (rooms.length === 0) {
    if (filter !== "all") {
      return (
        <EmptyState
          icon={ForumIcon}
          title="Nothing under this filter"
          description={FILTERED_EMPTY[filter] || "No conversations of this kind yet."}
          actionLabel={onClearFilter ? "Show all" : null}
          onAction={onClearFilter}
        />
      )
    }

    return (
      <EmptyState
        icon={ForumIcon}
        title="No conversations"
        description="Start a direct message, or open a topic thread with a client."
        actionLabel={onCreate ? "New conversation" : null}
        onAction={onCreate}
      />
    )
  }

  return (
    <List disablePadding sx={{ overflowY: "auto", flex: 1 }}>
      {rooms.map((room) => {
        const Icon = KIND_ICON[room.kind]
        const title = titleFor(room)
        const active = String(room.id) === String(activeId)

        return (
          <ListItemButton key={room.id} selected={active} onClick={() => onSelect(room.id)} sx={{ alignItems: "flex-start", py: 1.25 }}>
            <ListItemAvatar sx={{ minWidth: 48 }}>
              <Badge
                color="error"
                badgeContent={room.unread_count || 0}
                max={99}
                overlap="circular"
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <Avatar sx={{ width: 34, height: 34, bgcolor: avatarColor(title), fontSize: 13 }}>
                  {Icon ? <Icon sx={{ fontSize: 18 }} /> : initialsOf(title)}
                </Avatar>
              </Badge>
            </ListItemAvatar>

            <ListItemText
              primary={
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={room.unread_count ? 700 : 600} noWrap sx={{ flex: 1 }}>
                    {title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {relativeTime(room.last_message_at || room.created_at)}
                  </Typography>
                </Stack>
              }
              secondary={
                <>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {room.last_message_preview || "No messages yet"}
                  </Typography>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                    {/* Which client the conversation is about. Hidden for '0' —
                        "back office" on a back-office user's own threads is noise. */}
                    {room.client_id !== "0" && room.client_label ? (
                      <Chip size="small" label={room.client_label} sx={{ height: 18, fontSize: 10 }} />
                    ) : null}
                    {room.project_code ? (
                      <Chip size="small" variant="outlined" color="primary" label={room.project_code} sx={{ height: 18, fontSize: 10 }} />
                    ) : null}
                    {room.task_code ? (
                      <Chip size="small" variant="outlined" label={room.task_code} sx={{ height: 18, fontSize: 10 }} />
                    ) : null}
                  </Stack>
                </>
              }
              secondaryTypographyProps={{ component: "div" }}
            />
          </ListItemButton>
        )
      })}
    </List>
  )
}
